// @ts-strict-ignore
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  Arrange,
  Box,
  Button,
  Checkbox,
  Column,
  Columns,
  Link,
  Modal,
  Spacer,
  Typography,
  useToast,
} from "@aviary/components";
import { usePlatformRedirectMutation } from "@clinic/shared/data/mutations/PlatformRedirectMutation/platformRedirect.mutation";
import benefits from "@emerson/assets/images/rsoModal/image_144.jpg";
import linkedAccount from "@emerson/assets/images/rsoModal/image_145.jpg";
import { l } from "@emerson/locales/i18n";
import { navigateWithoutRouter } from "@helpers/navigateWithoutRouter/navigateWithoutRouter";
import { sharedRoutes } from "@shared/data/sharedRoutes";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";

import { useEnableSharedAccountMutation } from "./EnableSharedAccount.mutation";

import * as styles from "./RsoModal.styles";

interface Props {
  hasMultipleStorePlatforms: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const RsoModal = ({ hasMultipleStorePlatforms, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const isDesktop = tablet.greaterThan;

  const [acceptedAgreement, setAcceptedAgreement] = useState(false);
  const [successfullyEnabled, setSuccessfullyEnabled] = useState(false);
  const { makeToast } = useToast();

  const [enableSharedAccount, { loading }] = useEnableSharedAccountMutation({
    onCompleted: () => setSuccessfullyEnabled(true),
    onError: () => {
      onClose();
      makeToast("error", t(l.rso.error));
      setSuccessfullyEnabled(false);
    },
  });

  const [platformRedirect] = usePlatformRedirectMutation({
    onError: () => {
      onClose();
      makeToast("error", t(l.rso.errorRedirect));
    },
  });

  const exploreFullscript = async () => {
    const { data: redirectData, errors: redirectError } = await platformRedirect({
      variables: {
        input: {
          targetPlatformName: "FULLSCRIPT",
        },
      },
    });

    if (redirectError || redirectData.platformRedirect.errors.message) {
      makeToast("error", t(l.rso.errorRedirect));

      return;
    }

    navigateWithoutRouter(redirectData?.platformRedirect?.redirectPath);
  };

  const submitEnableSharedAccount = async () => {
    await enableSharedAccount({
      variables: {
        input: {
          requestedPlatform: "FULLSCRIPT",
        },
      },
    });
  };

  const showSuccess = (successfullyEnabled && !loading) || hasMultipleStorePlatforms;
  const renderSuccess = () => (
    <Arrange isFullWidth isFullHeight isVertical={!isDesktop}>
      <Columns css={styles.agreements.modalColumns} isMultiline={!isDesktop}>
        <Column columnWidth={5} isPaddingless>
          <Arrange
            justify="center"
            alignment="center"
            css={styles.agreements.successAccent}
            isVertical
          >
            <img src={linkedAccount} alt="linked account" />
          </Arrange>
        </Column>
        <Column isPaddingless>
          <Arrange css={styles.agreements.infoContent} isFullWidth>
            <Typography type={isDesktop ? "h2" : "h4"}>{t(l.rso.success.title)}</Typography>
            <Typography type="body">{t(l.rso.success.successCopy)}</Typography>
            <Typography type="body">{t(l.rso.success.accessCopy)}</Typography>
            <div css={styles.agreements.successConfirmation}>
              <Button onClick={exploreFullscript} isFullWidth={!isDesktop} intention="primary">
                {t(l.rso.success.next)}
              </Button>
            </div>
          </Arrange>
        </Column>
      </Columns>
    </Arrange>
  );

  const renderAgreement = () => (
    <Arrange
      css={styles.agreements.infoModal}
      isFullWidth
      isFullHeight
      isVertical={!isDesktop}
      spacing="none"
    >
      <Columns isMultiline={!isDesktop}>
        <Column columnWidth={5} isPaddingless>
          <Arrange
            justify="center"
            alignment="center"
            css={styles.agreements.infoAccent}
            isVertical
          >
            <img src={benefits} alt="benefits" />
          </Arrange>
        </Column>
        <Column isPaddingless>
          <Arrange css={styles.agreements.infoContent} isFullWidth>
            <Typography type={isDesktop ? "h2" : "h4"}>{t(l.rso.agreement.title)}</Typography>
            <Typography type="body">{t(l.rso.agreement.agreementCopy)}</Typography>

            <Typography type="h5">{t(l.rso.agreement.betterAccess)}</Typography>
            <Typography type="body">{t(l.rso.agreement.betterAccessCopy)}</Typography>

            <Typography type="h5">{t(l.rso.agreement.clearerClinical)}</Typography>
            <Typography type="body">{t(l.rso.agreement.clearerClinicalCopy)}</Typography>
            <Typography type="body">{t(l.rso.agreement.accessCopy)}</Typography>
            <Spacer height="oneHalf" />
            <Box isColor="system" padding="small">
              <Arrange spacing="small" isWrapped={false}>
                <Checkbox
                  checked={acceptedAgreement}
                  onChange={() => setAcceptedAgreement(!acceptedAgreement)}
                />
                <Trans
                  i18nKey={l.rso.agreement.terms}
                  components={{
                    termsLink: (
                      <Link
                        isColor="system"
                        css={styles.agreements.linkBtn}
                        noExternalIcon
                        href={sharedRoutes.external.termsOfService}
                      />
                    ),
                    privacyLink: (
                      <Link
                        isColor="system"
                        css={styles.agreements.linkBtn}
                        noExternalIcon
                        href={sharedRoutes.external.privacyPolicy}
                      />
                    ),
                  }}
                />
              </Arrange>
            </Box>
            <div css={styles.agreements.infoConfirmation}>
              <Button
                disabled={!acceptedAgreement || loading}
                isFullWidth={!isDesktop}
                intention="primary"
                onClick={submitEnableSharedAccount}
              >
                {t(l.rso.agreement.next)}
              </Button>
            </div>
          </Arrange>
        </Column>
      </Columns>
    </Arrange>
  );

  return (
    <Modal
      size={showSuccess ? "large" : "xlarge"}
      isOverflowVisible={false}
      isOpen={isOpen}
      onClose={onClose}
    >
      {showSuccess ? renderSuccess() : renderAgreement()}
    </Modal>
  );
};

export { RsoModal };
